import { useState, useEffect, useCallback } from 'react'
import { Box, Flex, Text, VStack, Grid, Modal, ConfirmDialog, useT } from '@minetrace/ui'
import {
  useFormColors,
  TrackedInput,
  TrackedSelect,
  TrackedDatePicker,
  FileInput,
  withFormProvider,
} from '@minetrace/ui/forms'
import { useStep4bLegalDocumentsForm, type Step4bLegalDocumentFormValues } from './useStep4bLegalDocumentsForm'
import {
  LuChevronDown as ChevronDown,
  LuChevronRight as ChevronRight,
  LuFileText as FileText,
  LuPlus as Plus,
  LuPencil as Pencil,
  LuTrash2 as Trash2,
} from 'react-icons/lu'
import type { MiningRightResponse, LegalDocumentRecordResponse } from '../../../api/models/response/proveedor'
import MiningRightDataWarning from '../../../components/MiningRightDataWarning'
import type { AddLegalDocumentRecordRequest } from '../../../api/models/request/proveedor'
import type { UseFormReturn } from 'react-hook-form'
import { LEGAL_DOCUMENT_TYPES, IGAFOM_DOCUMENT_STATUSES, enumLabel } from '../../../data/enums'
import DocumentoDescarga from '../../../components/DocumentoDescarga'
import { useDeleteWarning } from '../../../lib/modificationMode'

// Reusa los mismos estados que IGAFOM (Presentado/EnEvaluación/Aprobado/Observado/Rechazado).
const DOCUMENT_STATUS_TIDS: Record<number, string> = {
  1: 'proveedores.wizard.step4.statusEnEvaluacion',
  2: 'proveedores.wizard.step4.statusAprobado',
  3: 'proveedores.wizard.step4.statusObservado',
  4: 'proveedores.wizard.step4.statusRechazado',
}
const DOCUMENT_STATUSES = IGAFOM_DOCUMENT_STATUSES.map(opt => ({
  ...opt,
  labelTid: DOCUMENT_STATUS_TIDS[opt.value],
}))

// ── Modal Form (inner) ──

interface LegalDocumentFormInnerProps {
  formMethods: UseFormReturn<Step4bLegalDocumentFormValues>
  isEditing: boolean
  isBusy: boolean
  onSave: (values: Step4bLegalDocumentFormValues, receiptFile?: File, documentFile?: File) => void
  onCancel: () => void
  onRegisterSubmit?: (fn: () => void) => void
}

const LegalDocumentFormInner = withFormProvider<Step4bLegalDocumentFormValues, LegalDocumentFormInnerProps>(
  ({ formMethods, isEditing, isBusy, onSave, onRegisterSubmit }) => {
    const { t } = useT()
    const [receiptFile, setReceiptFile] = useState<File | null>(null)
    const [documentFile, setDocumentFile] = useState<File | null>(null)
    const [fileErrors, setFileErrors] = useState<{ receipt?: string; document?: string }>({})

    const handleFormSubmit = useCallback(async () => {
      const isValid = await formMethods.trigger()
      const errors: { receipt?: string; document?: string } = {}
      if (!isEditing && !receiptFile) errors.receipt = t('proveedores.wizard.step4b.receiptRequired')
      if (!isEditing && !documentFile) errors.document = t('proveedores.wizard.step4b.documentRequired')
      setFileErrors(errors)
      if (!isValid || Object.keys(errors).length > 0) return
      onSave(formMethods.getValues(), receiptFile ?? undefined, documentFile ?? undefined)
    }, [formMethods, isEditing, receiptFile, documentFile, onSave, t])

    useEffect(() => {
      onRegisterSubmit?.(() => handleFormSubmit())
    }, [onRegisterSubmit, handleFormSubmit])

    const today = new Date().toISOString().split('T')[0]

    return (
      <Box opacity={isBusy ? 0.5 : 1} pointerEvents={isBusy ? 'none' : 'auto'} transition="opacity 0.2s">
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
          <TrackedSelect name="documentType" labelTid="proveedores.wizard.step4b.documentTypeLabel" options={LEGAL_DOCUMENT_TYPES} placeholderTid="proveedores.wizard.step4b.selectPlaceholder" />
          <TrackedSelect name="documentStatus" labelTid="proveedores.wizard.step4b.documentStatusLabel" options={DOCUMENT_STATUSES} placeholderTid="proveedores.wizard.step4b.selectPlaceholder" />
          <TrackedDatePicker name="presentationDate" labelTid="proveedores.wizard.step4b.presentationDateLabel" maxDate={today} />
          <TrackedDatePicker name="expirationDate" labelTid="proveedores.wizard.step4b.expirationDateLabel" />
          <TrackedInput name="fileNumber" labelTid="proveedores.wizard.step4b.fileNumberLabel" placeholderTid="proveedores.wizard.step4b.fileNumberPlaceholder" maxLength={100} />
        </Grid>

        <Grid templateColumns="1fr" gap={4} mt={4}>
          <FileInput
            labelTid="proveedores.wizard.step4b.receiptLabel"
            required
            accept=".pdf,.jpg"
            maxSizeMb={5}
            value={receiptFile}
            onChange={(f) => { setReceiptFile(f); setFileErrors(prev => ({ ...prev, receipt: undefined })) }}
            error={fileErrors.receipt}
          />
          <FileInput
            labelTid="proveedores.wizard.step4b.documentLabel"
            required
            accept=".pdf"
            maxSizeMb={20}
            value={documentFile}
            onChange={(f) => { setDocumentFile(f); setFileErrors(prev => ({ ...prev, document: undefined })) }}
            error={fileErrors.document}
          />
        </Grid>
      </Box>
    )
  },
  useStep4bLegalDocumentsForm,
)

// ── Main Component ──

interface Step4bLegalDocumentsProps {
  mode: 'edit' | 'readonly'
  companyId: string | null
  savedMiningRights: MiningRightResponse[]
  onAdd: (mrId: string, data: AddLegalDocumentRecordRequest, receiptFile?: File, documentFile?: File) => Promise<void>
  onUpdate: (mrId: string, ldId: string, data: AddLegalDocumentRecordRequest, receiptFile?: File, documentFile?: File) => Promise<void>
  onRemove: (mrId: string, ldId: string) => Promise<void>
}

export default function Step4bLegalDocuments({ mode, savedMiningRights, onAdd, onUpdate, onRemove }: Step4bLegalDocumentsProps) {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly'

  const [expandedRights, setExpandedRights] = useState<Set<string>>(new Set())
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMrId, setModalMrId] = useState<string | null>(null)
  const [editingRecord, setEditingRecord] = useState<LegalDocumentRecordResponse | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [submitFn, setSubmitFn] = useState<(() => void) | null>(null)
  const deleteWarning = useDeleteWarning()
  const [deleteTarget, setDeleteTarget] = useState<{ mrId: string; ldId: string; name: string } | null>(null)

  const toggleRight = (id: string) => {
    setExpandedRights(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
  }

  const openAddModal = (mrId: string) => {
    setModalMrId(mrId)
    setEditingRecord(null)
    setModalOpen(true)
  }

  const openEditModal = (mrId: string, rec: LegalDocumentRecordResponse) => {
    setModalMrId(mrId)
    setEditingRecord(rec)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setModalMrId(null)
    setEditingRecord(null)
  }

  const handleSave = async (values: Step4bLegalDocumentFormValues, receiptFile?: File, documentFile?: File) => {
    if (!modalMrId) return
    const data: AddLegalDocumentRecordRequest = {
      documentType: values.documentType || undefined,
      presentationDate: values.presentationDate || undefined,
      expirationDate: values.expirationDate || undefined,
      fileNumber: values.fileNumber || undefined,
      documentStatus: values.documentStatus || undefined,
    }
    setIsBusy(true)
    try {
      if (editingRecord) {
        await onUpdate(modalMrId, editingRecord.id, data, receiptFile, documentFile)
      } else {
        await onAdd(modalMrId, data, receiptFile, documentFile)
      }
      closeModal()
    } finally { setIsBusy(false) }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setIsBusy(true)
    try {
      await onRemove(deleteTarget.mrId, deleteTarget.ldId)
      setDeleteTarget(null)
    } finally { setIsBusy(false) }
  }

  return (
    <VStack gap={6} alignItems="stretch">
      {savedMiningRights.length === 0 ? (
        <Box p={8} textAlign="center" borderRadius="xl" border="1px dashed" borderColor="gray.200">
          <FileText size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <Text tid="proveedores.wizard.step4b.emptyMessage" fontSize="sm" color={c.textMuted} />
        </Box>
      ) : (
        <VStack gap={4} alignItems="stretch">
          {savedMiningRights.map(right => {
            const isExpanded = expandedRights.has(right.id)
            const records = right.legalDocumentRecords ?? []

            return (
              <Box key={right.id} borderRadius="xl" border="1px solid" borderColor={c.border} overflow="hidden">
                <Flex px={5} py={3} bg={c.bgHover} alignItems="center" justifyContent="space-between" cursor="pointer" onClick={() => toggleRight(right.id)}>
                  <Flex alignItems="center" gap={2}>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Text fontSize="sm" fontWeight="600" color={c.text}>{t('common.miningRightLabel')}: {right.name || right.uniqueCode}</Text>
                    <Text fontSize="xs" color={c.textMuted}>({right.uniqueCode})</Text>
                  </Flex>
                  <Flex alignItems="center" gap={3}>
                    <MiningRightDataWarning hasBoundary={right.hasBoundary} componentCount={right.components?.length ?? 0} />
                    <Text tid="proveedores.wizard.step4b.recordsCount" values={{ count: records.length }} fontSize="xs" color={c.textMuted} />
                  </Flex>
                </Flex>

                {isExpanded && (
                  <Box px={5} py={4}>
                    {!isReadonly && (
                      <Flex mb={4}>
                        <Box
                          as="button" type="button" display="flex" alignItems="center" gap={2}
                          px={4} py={2} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
                          cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => openAddModal(right.id)}
                        >
                          <Plus size={14} />{t('proveedores.wizard.step4b.addButton')}
                        </Box>
                      </Flex>
                    )}

                    {records.length > 0 ? (
                      <VStack gap={3} alignItems="stretch">
                        {records.map(rec => (
                          <Box key={rec.id} p={4} bg={c.bgInput} borderRadius="lg" border="1px solid" borderColor={c.border}>
                            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
                              <Box>
                                <Text tid="proveedores.wizard.step4b.documentType" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{enumLabel(LEGAL_DOCUMENT_TYPES, rec.documentType)}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4b.fileNumber" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.fileNumber ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4b.documentStatus" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.documentStatusName ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4b.presentationDate" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.presentationDate ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4b.expirationDate" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.expirationDate ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4b.receipt" fontSize="xs" color={c.textMuted} />
                                {rec.receiptFile ? (
                                  <DocumentoDescarga caption="Cargo de Presentación" storagePath={rec.receiptFile.storagePath} fileName={rec.receiptFile.originalFileName} />
                                ) : <Text tid="proveedores.wizard.step4b.noFile" fontSize="sm" color={c.textMuted} />}
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4b.document" fontSize="xs" color={c.textMuted} />
                                {rec.documentFile ? (
                                  <DocumentoDescarga caption={rec.documentTypeName ?? 'Documento legal'} storagePath={rec.documentFile.storagePath} fileName={rec.documentFile.originalFileName} />
                                ) : <Text tid="proveedores.wizard.step4b.noFile" fontSize="sm" color={c.textMuted} />}
                              </Box>
                            </Grid>

                            {!isReadonly && (
                              <Flex mt={3} gap={2} justifyContent="flex-end">
                                <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={3} py={1.5} bg={`${c.accent}15`} color={c.accent} borderRadius="lg" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: `${c.accent}25` }} onClick={() => openEditModal(right.id, rec)}>
                                  <Pencil size={12} />{t('proveedores.wizard.step4b.edit')}
                                </Box>
                                <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={3} py={1.5} bg="red.50" color="red.600" borderRadius="lg" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: 'red.100' }} onClick={() => setDeleteTarget({ mrId: right.id, ldId: rec.id, name: rec.fileNumber ?? t('proveedores.wizard.step4b.defaultRecordName') })}>
                                  <Trash2 size={12} />{t('proveedores.wizard.step4b.delete')}
                                </Box>
                              </Flex>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    ) : (
                      <Text tid="proveedores.wizard.step4b.noRecordsForRight" fontSize="sm" color={c.textMuted} textAlign="center" py={4} />
                    )}
                  </Box>
                )}
              </Box>
            )
          })}
        </VStack>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingRecord ? t('proveedores.wizard.step4b.modalTitleEdit') : t('proveedores.wizard.step4b.modalTitleNew')}
        subtitle={modalMrId ? savedMiningRights.find(r => r.id === modalMrId)?.name : undefined}
        size="lg"
        footer={
          <>
            <Box as="button" type="button" px={5} py={2.5} borderRadius="md" fontSize="sm" fontWeight="600"
              color={c.textMuted} border="1px solid" borderColor={c.border}
              cursor="pointer" _hover={{ bg: c.bgHover }} onClick={closeModal}
            >
              {t('proveedores.wizard.step4b.cancel')}
            </Box>
            <Box
              as="button" type="button" display="flex" alignItems="center" gap={2}
              px={6} py={2.5} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
              cursor={isBusy ? 'not-allowed' : 'pointer'} opacity={isBusy ? 0.6 : 1}
              _hover={{ opacity: 0.9 }} onClick={() => submitFn?.()} disabled={isBusy}
            >
              {isBusy ? t('proveedores.wizard.step4b.saving') : editingRecord ? t('proveedores.wizard.step4b.update') : <><Plus size={14} /> {t('proveedores.wizard.step4b.register')}</>}
            </Box>
          </>
        }
      >
        <LegalDocumentFormInner
          key={editingRecord?.id ?? 'new'}
          defaultValues={editingRecord ? {
            documentType: editingRecord.documentType ?? 0,
            presentationDate: editingRecord.presentationDate ?? '',
            expirationDate: editingRecord.expirationDate ?? '',
            fileNumber: editingRecord.fileNumber ?? '',
            documentStatus: editingRecord.documentStatus ?? 0,
          } : undefined}
          isEditing={Boolean(editingRecord)}
          isBusy={isBusy}
          onSave={handleSave}
          onCancel={closeModal}
          onRegisterSubmit={(fn) => setSubmitFn(() => fn)}
        />
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t('proveedores.wizard.step4b.deleteTitle')}
        confirmLabel={t('proveedores.wizard.step4b.deleteConfirm')}
        variant="danger"
        isLoading={isBusy}
      >
        ¿Está seguro que desea eliminar el documento <Text as="span" fontWeight="600">{deleteTarget?.name}</Text>? {deleteWarning}
      </ConfirmDialog>
    </VStack>
  )
}
